/**
 * Inventario de Documentos — hub de matrículas y placas entre sucursales.
 *
 * Seis secciones sobre pantallas propias; este archivo solo navega:
 *   Revisión      consultamatricula   (dónde está cada documento)
 *   Crear envío   enviodocumentos     (N chasis en un envío, con guía)
 *   Envíos        enviodocumentos     (recibir por checklist / seguimiento)
 *   Devoluciones  devolvermatricula
 *   Seguimiento   estadomatriculas
 *   Entrega       entregamatricula    (al cliente)
 *
 * La creación de un chasis a la vez y la recepción de renglones sueltos que
 * vivían aquí (775 líneas) se reemplazaron por el envío con cabecera; la
 * recepción anterior (recepciondocumentos) sigue accesible desde Envíos
 * mientras queden transferencias sin envío en vuelo.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import DoneAllRounded from "@mui/icons-material/DoneAllRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import UndoRounded from "@mui/icons-material/UndoRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import { FP, PageHeader } from "components/financepro";
import { useApi } from "lib/api";
import ConsultaMatriculas from "./consultamatricula";
import EntregaMatriculas from "./entregamatricula";
import Recepciondocumentos from "./recepciondocumentos";
import DevolverMatricula from "./devolvermatricula";
import EstadoMatriculas from "./estadomatriculas";
import { CrearEnvio, Envios } from "./enviodocumentos";

const SECCIONES = [
  { id: "revision", title: "Revisión", icon: DoneAllRounded },
  { id: "crear", title: "Crear envío", icon: LocalShippingOutlinedIcon },
  { id: "envios", title: "Envíos / Recepción", icon: Inventory2OutlinedIcon },
  { id: "devoluciones", title: "Devoluciones", icon: UndoRounded },
  { id: "seguimiento", title: "Seguimiento", icon: DescriptionRounded },
  { id: "entrega", title: "Entrega", icon: AssignmentTurnedInOutlinedIcon },
];

export default function Transferenciadocumentos() {
  const { post } = useApi();
  const [seccion, setSeccion] = useState("revision");
  const sucursal = localStorage.getItem("sucursal") || "-";
  // Remontar Envíos al crear uno (refresca el listado si el usuario salta).
  const [enviosKey, setEnviosKey] = useState(0);
  // Envíos en tránsito hacia mi sucursal: el contador de la pestaña Envíos
  // es el recordatorio de que hay algo que recibir.
  const [porRecibir, setPorRecibir] = useState(0);

  const contarPorRecibir = async () => {
    try {
      const { ok, data } = await post("/api/envio_listar", {
        direccion: "entrantes",
        estado: "en transito",
      });
      if (ok) setPorRecibir((data.envios || []).length + (data.legadas_en_transito ? 1 : 0));
    } catch (e) {
      // sin contador: la pestaña sigue funcionando
    }
  };

  useEffect(() => {
    contarPorRecibir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seccion, enviosKey]);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Inventario de Documentos"
        subtitle="Matrículas y placas entre sucursales"
        cta={
          <Chip
            label={`Sucursal: ${sucursal}`}
            sx={{
              fontWeight: 700,
              color: FP.color.navy,
              bgcolor: FP.color.accentBlueSoft,
              border: `1px solid ${FP.color.border}`,
            }}
          />
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Navegación segmentada: una barra, no seis tarjetas. */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            p: 0.6,
            mb: 2.6,
            bgcolor: FP.color.cardBg,
            border: `1px solid ${FP.color.border}`,
            borderRadius: 2.5,
            boxShadow: FP.shadow.soft,
            overflowX: "auto",
          }}
        >
          {SECCIONES.map((s) => {
            const Icon = s.icon;
            const activa = seccion === s.id;
            const badge = s.id === "envios" && porRecibir > 0 ? porRecibir : null;
            return (
              <Box
                key={s.id}
                role="button"
                tabIndex={0}
                onClick={() => setSeccion(s.id)}
                onKeyDown={(e) => e.key === "Enter" && setSeccion(s.id)}
                sx={{
                  flex: { xs: "0 0 auto", md: 1 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.9,
                  px: 1.8,
                  py: 1.1,
                  borderRadius: 1.8,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  fontWeight: 700,
                  fontSize: "0.86rem",
                  color: activa ? "#FFFFFF" : FP.color.textSecondary,
                  bgcolor: activa ? FP.color.navy : "transparent",
                  transition: "background-color .15s, color .15s",
                  "&:hover": { bgcolor: activa ? FP.color.navySoft : FP.color.surface },
                  "&:focus-visible": {
                    outline: `2px solid ${FP.color.accentBlue}`,
                    outlineOffset: 1,
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
                {s.title}
                {badge != null && (
                  <Box
                    component="span"
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.7,
                      borderRadius: 10,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      bgcolor: activa ? "#FFFFFF" : "#E65100",
                      color: activa ? FP.color.navy : "#FFFFFF",
                    }}
                  >
                    {badge}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {seccion === "revision" && <ConsultaMatriculas />}
        {seccion === "crear" && <CrearEnvio onCreado={() => setEnviosKey((k) => k + 1)} />}
        {seccion === "envios" && (
          <Envios
            key={enviosKey}
            direccionInicial="entrantes"
            renderLegado={() => <Recepciondocumentos tipo="documentos" />}
          />
        )}
        {seccion === "devoluciones" && <DevolverMatricula />}
        {seccion === "seguimiento" && <EstadoMatriculas />}
        {seccion === "entrega" && <EntregaMatriculas />}
      </Box>
    </Box>
  );
}
